import React, { ReactNode } from "react";
import { useI18n } from "i18n";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Select } from "@remo-co/ui-core/src/components/Select";
import { Typography } from "@remo-co/ui-core/src/components/Typography";

import classNames from "classnames";
import {
  SerializedDevice,
  decodeDevice,
  encodeDevice,
  parseDeviceLabel,
} from "modules/deviceInfo";
import { useStyles } from "./styles";

interface Props {
  selected: SerializedDevice | null;
  devices: SerializedDevice[];
  onChange: (value: SerializedDevice) => void;
  disabled?: boolean;
  dark?: boolean;
  outerLabel?: string;
  label?: string;
  selectAriaLabel?: string;
  fullWidth?: boolean;
  icon?: ReactNode;
  vertical?: boolean;
}

const noValue = "None";

const DeviceSelector = ({
  selected,
  devices,
  onChange,
  label,
  outerLabel,
  selectAriaLabel = "",
  dark,
  disabled = false,
  fullWidth,
  icon,
  vertical = false,
}: Props): JSX.Element | null => {
  const styles = useStyles();
  const { t } = useI18n();

  const handleSelectChange = (encodedDevice: string) => {
    const device = decodeDevice(encodedDevice);

    if (device) {
      onChange(device);
    }
  };

  const selectOptions = React.useMemo(
    () => [
      ...(devices?.map((device) => ({
        label: parseDeviceLabel(device.label),
        value: encodeDevice(device),
      })) ?? []),
      { label: t("default"), value: noValue, disabled: true },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devices],
  );

  if (!devices?.[0] && !disabled) {
    return null;
  }

  let value: string;

  if (selected) {
    value = encodeDevice(selected);
  } else if (devices && devices[0]) {
    value = encodeDevice(devices[0]);
  } else {
    return null;
  }

  const addIconOrOuterLabel = Boolean(icon || outerLabel);

  return (
    <Box
      tabIndex={-1}
      data-testid="device-select-container"
      display="flex"
      flexDirection={vertical ? "column" : "row"}
      justifyContent={vertical ? "flex-start" : "space-between"}
      className={classNames({ [styles.deviceCtn]: !fullWidth, dark })}
    >
      {addIconOrOuterLabel && (
        <Container
          flex
          className={styles.outerLabelContainer}
          justifyContent="flex-start"
        >
          {icon && icon}
          {outerLabel && <Typography variant="h5">{outerLabel}</Typography>}
        </Container>
      )}
      <Select
        aria-label={t(selectAriaLabel)}
        data-testid="device-selector"
        label={label}
        disabled={disabled}
        value={disabled ? noValue : value}
        onChange={handleSelectChange}
        options={selectOptions}
        size="small"
        fullWidth
      />
    </Box>
  );
};

export default DeviceSelector;
